import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'react-toastify';
import * as api from './api';
import type { ListTagsParams, TagFormValues } from './schema';

const ROOT_KEY = 'tags';
const listKey = (params: ListTagsParams) =>
  [ROOT_KEY, 'list', params] as const;
const detailKey = (id: string) => [ROOT_KEY, 'detail', id] as const;
const optionsKey = [ROOT_KEY, 'options'] as const;

function describeError(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const data = err.response?.data as
      | { message?: string | string[]; error?: string }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg)) return msg.join(', ');
    if (typeof msg === 'string' && msg.length > 0) return msg;
    if (data?.error) return data.error;
  }
  return fallback;
}

export function useTags(params: ListTagsParams) {
  return useQuery({
    queryKey: listKey(params),
    queryFn: () => api.listTags(params),
    placeholderData: (prev) => prev,
  });
}

export function useTag(id: string | null) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.getTag(id as string),
    enabled: !!id,
  });
}

// Active-only options for the news-form multi-select; tags rarely change so
// keep this cached aggressively.
export function useTagOptions() {
  return useQuery({
    queryKey: optionsKey,
    queryFn: () => api.getTagOptions(),
    staleTime: 60 * 60 * 1000,
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (values: TagFormValues) => api.createTag(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      toast.success('Tag created');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to create tag')),
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, values }: { id: string; values: TagFormValues }) =>
      api.updateTag(id, values),
    onSuccess: (updated) => {
      // Same reasoning as news articles: update response lacks `translations`,
      // so invalidate rather than merge.
      queryClient.invalidateQueries({ queryKey: [ROOT_KEY, 'list'] });
      queryClient.invalidateQueries({ queryKey: detailKey(updated.id) });
      queryClient.invalidateQueries({ queryKey: optionsKey });
      toast.success('Tag updated');
    },
    onError: (err) => toast.error(describeError(err, 'Failed to update tag')),
  });
}
